import { useState, type ReactNode } from 'react';
import { ChevronRight, Map as MapIcon, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { UscgSymbol } from './utils/uscgSymbology';
import { InternalObjectSymbol } from './InternalObjectSymbol';
import { FeatureSymbol } from './FeatureSymbol';
import type { Geometry } from 'geojson';

export type FeatureItemProps = {
  internalFeature?: {
    featureId: string; // icsIncidentLocation.id or generated pendingGraphicId
    key: string;
    title: string;
    resourceType: UscgSymbol;
    properties: unknown;
    updatedAt?: string | null;
    geometry: Geometry;
  };
  externalFeature?: {
    featureId: string; // external feature OBJECTID or generated id
    symbol?: __esri.SymbolUnion | null;
  };
  title: ReactNode;
  onZoom: () => void | Promise<void>;
  children: ReactNode;
  onEdit?: (featureId: string) => Promise<void> | void;
  onDelete?: (featureId: string) => Promise<void> | void;
  isEditing?: boolean;
};

export function FeatureItem({
  internalFeature,
  externalFeature,
  title,
  onZoom,
  children,
  onEdit,
  onDelete,
  isEditing = false,
}: FeatureItemProps) {
  const [open, setOpen] = useState(false);
  const featureId = (internalFeature?.featureId ??
    externalFeature?.featureId) as string;

  const symbol = internalFeature ? (
    <InternalObjectSymbol
      resourceType={internalFeature.resourceType}
      properties={internalFeature.properties}
    />
  ) : (
    <FeatureSymbol symbol={externalFeature?.symbol} />
  );

  return (
    <li className="tw:!m-0 tw:!w-full tw:list-none tw:p-0">
      <Card
        size="sm"
        className={cn(
          'dark tw:w-full tw:gap-0 tw:!border tw:!border-solid tw:!border-foreground/35 tw:bg-card tw:py-0 tw:text-left tw:text-foreground tw:shadow-sm',
          isEditing && 'tw:ring-2 tw:ring-primary'
        )}
      >
        <Collapsible open={open} onOpenChange={setOpen}>
          <div className="tw:flex tw:items-center tw:gap-1 tw:px-2 tw:py-1.5">
            <CollapsibleTrigger
              render={
                <button
                  type="button"
                  className="tw:flex tw:min-w-0 tw:flex-1 tw:items-center tw:gap-2 tw:rounded-md tw:px-1 tw:py-0.5 tw:text-left tw:text-foreground tw:hover:bg-accent/60"
                >
                  <ChevronRight
                    className={cn(
                      'tw:size-3.5 tw:shrink-0 tw:text-muted-foreground tw:transition-transform',
                      open && 'tw:rotate-90'
                    )}
                  />
                  {symbol}
                  <span className="tw:min-w-0 tw:flex-1 tw:truncate tw:text-left tw:text-xs tw:font-medium">
                    {title}
                  </span>
                </button>
              }
            />
            <div className="tw:flex tw:shrink-0 tw:items-center tw:gap-0.5">
              {onEdit ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  aria-label="Edit object"
                  title="Edit object"
                  onClick={(event) => {
                    event.stopPropagation();
                    void onEdit(featureId);
                  }}
                >
                  <Pencil className="tw:size-3.5" />
                </Button>
              ) : null}
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                aria-label="Zoom to map location"
                title="Zoom to map location"
                onClick={(event) => {
                  event.stopPropagation();
                  void onZoom();
                }}
              >
                <MapIcon className="tw:size-3.5" />
              </Button>
              {onDelete ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  aria-label="Delete object"
                  title="Delete object"
                  onClick={(event) => {
                    event.stopPropagation();
                    void onDelete(featureId);
                  }}
                >
                  <Trash2 className="tw:size-3.5" />
                </Button>
              ) : null}
            </div>
          </div>
          <CollapsibleContent className="tw:space-y-1.5 tw:border-t tw:border-border tw:px-2.5 tw:py-2 tw:text-xs tw:text-foreground">
            {children}
          </CollapsibleContent>
        </Collapsible>
      </Card>
    </li>
  );
}
